import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polygon, Circle, Marker, Popup, useMapEvents, Polyline, useMap, ScaleControl } from 'react-leaflet';
import { LatLng, LatLngBounds } from 'leaflet';
import L from 'leaflet';
import { Farm, Pod, ArtificialReef, MapMode, ReefSurroundings } from '../types';
import { apiService } from '../apiService';
import { FarmSidebar } from './FarmSidebar';
import { NetworkModal } from './NetworkModal';
import { InstructionsPanel } from './InstructionsPanel';
import { BackgroundPanel } from './BackgroundPanel';
import './Map.css';

// Spanish coast bounds (focused on Galicia region)
const SPANISH_COAST_BOUNDS = new LatLngBounds(
  [42.0, -9.5], // Southwest corner
  [43.5, -8.0]  // Northeast corner
);

const INITIAL_CENTER: [number, number] = [42.75, -8.75]; // Center of Galicia coast
const INITIAL_ZOOM = 10;

// Component to prevent map viewport resets
function MapViewportPreserver() {
  const map = useMap();
  const [initialViewSet, setInitialViewSet] = useState(false);

  useEffect(() => {
    if (!initialViewSet) {
      // Only set the initial view once
      map.setView(INITIAL_CENTER, INITIAL_ZOOM);
      setInitialViewSet(true);
    }
  }, [map, initialViewSet]);

  return null;
}

interface MapProps {
  mode: MapMode;
  setMode: (mode: MapMode) => void;
}

// Component for draggable reefs
function DraggableReef({ 
  reef, 
  onDrag,
  onClick,
  isSelected,
  reefSurroundings,
  onDelete,
  onShowNetwork,
  isEditing,
  editFormData,
  onFormChange,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onPredictBiodiversity,
  predictionResult,
  predictionLoading,
  mode
}: { 
  reef: any; 
  onDrag: (reefId: number, newLatlng: any) => void;
  onClick: (reef: any) => void;
  isSelected: boolean;
  reefSurroundings: any;
  onDelete: (reefId: number) => void;
  onShowNetwork: (reef: any) => void;
  isEditing: boolean;
  editFormData: any;
  onFormChange: (field: string, value: any) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onStartEdit: () => void;
  onPredictBiodiversity: (reef: any) => void;
  predictionResult: { abundance: number; richness: number } | null;
  predictionLoading: boolean;
  mode: string;
}) {
  const markerRef = useRef<any>(null);
  
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          onDrag(reef.id, marker.getLatLng());
        }
      },
      click() {
        onClick(reef);
      }
    }),
    [reef, onDrag, onClick],
  );

  // Create custom icon for reefs - red square
  const reefIcon = L.divIcon({
    className: 'reef-marker',
    html: `<div style="
      width: 20px; 
      height: 20px; 
      background-color: #ff4444; 
      border: 2px solid #cc0000; 
      display: flex; 
      align-items: center; 
      justify-content: center;
      font-size: 10px;
      font-weight: bold;
      color: white;
      cursor: move;
    ">R</div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={[reef.position.coordinates[1], reef.position.coordinates[0]]}
      ref={markerRef}
      icon={reefIcon}
    >
      <Popup>
        <div>
          {isEditing ? (
            <div>
              <h3>Edit Reef</h3>
              <div style={{ marginBottom: '10px' }}>
                <label>Name:</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => onFormChange('name', e.target.value)}
                  style={{ width: '100%', marginTop: '5px' }}
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>Fish Species Count:</label>
                <input
                  type="number"
                  value={editFormData.fish_species_count}
                  onChange={(e) => onFormChange('fish_species_count', parseInt(e.target.value) || 0)}
                  style={{ width: '100%', marginTop: '5px' }}
                  min="0"
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>Detection Range (meters):</label>
                <input
                  type="number"
                  value={editFormData.detection_range}
                  onChange={(e) => onFormChange('detection_range', parseInt(e.target.value) || 0)}
                  style={{ width: '100%', marginTop: '5px' }}
                  min="0"
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>Volume (cm³):</label>
                <input
                  type="number"
                  value={editFormData.volume_in_cm3 || ''}
                  onChange={(e) => onFormChange('volume_in_cm3', parseInt(e.target.value) || null)}
                  style={{ width: '100%', marginTop: '5px' }}
                  min="0"
                  placeholder="Optional"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button onClick={onSaveEdit} style={{ backgroundColor: '#4CAF50', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px' }}>
                  Save
                </button>
                <button onClick={onCancelEdit} style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px' }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h3>{reef.name}</h3>
              <p>Fish Species: {reef.fish_species_count}</p>
              <p>Detection Range: {reef.detection_range}m</p>
              {reef.volume_in_cm3 && <p>Volume: {reef.volume_in_cm3} cm³</p>}
              
              {predictionResult && (
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '5px' }}>
                  <h4 style={{ margin: '0 0 5px 0' }}>Predicted Biodiversity:</h4>
                  <p style={{ margin: '5px 0' }}><strong>Abundance:</strong> {predictionResult.abundance.toFixed(2)}</p>
                  <p style={{ margin: '5px 0' }}><strong>Richness:</strong> {predictionResult.richness.toFixed(2)}</p>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '10px' }}>
                <button onClick={() => onDelete(reef.id)}>Delete Reef</button>
                <button onClick={() => onShowNetwork(reef)}>Show Network</button>
                {reef.volume_in_cm3 && (
                  <button 
                    onClick={() => onPredictBiodiversity(reef)} 
                    disabled={predictionLoading}
                    style={{ backgroundColor: '#2196F3', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px' }}
                  >
                    {predictionLoading ? 'Loading...' : 'Predict Biodiversity'}
                  </button>
                )}
                {mode === 'edit-reef' && (
                  <button onClick={onStartEdit} style={{ backgroundColor: '#FF9800', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px' }}>
                    Edit
                  </button>
                )}
              </div>
            </div>
          )}
          {isSelected && reefSurroundings && !isEditing && (
            <div className="reef-surroundings">
              <h4>Nearby Objects:</h4>
              {reefSurroundings.nearby_farms && reefSurroundings.nearby_farms.length > 0 && (
                <div>
                  <strong>Farms:</strong>
                  <ul>
                    {reefSurroundings.nearby_farms.map((farm: any) => (
                      <li key={farm.id}>{farm.name} {farm.distance ? `(${farm.distance.toFixed(0)}m)` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              {reefSurroundings.nearby_pods && reefSurroundings.nearby_pods.length > 0 && (
                <div>
                  <strong>Pods:</strong>
                  <ul>
                    {reefSurroundings.nearby_pods.map((pod: any) => (
                      <li key={pod.id}>{pod.name} {pod.distance ? `(${pod.distance.toFixed(0)}m)` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              {reefSurroundings.nearby_reefs && reefSurroundings.nearby_reefs.length > 0 && (
                <div>
                  <strong>Other Reefs:</strong>
                  <ul>
                    {reefSurroundings.nearby_reefs.map((otherReef: any) => (
                      <li key={otherReef.id}>{otherReef.name} {otherReef.distance ? `(${otherReef.distance.toFixed(0)}m)` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
}

// Component for draggable pods
function DraggablePod({ 
  pod, 
  pods,
  onDrag 
}: { 
  pod: any; 
  pods: any[];
  onDrag: (podId: number, newLatlng: any) => void;
}) {
  const markerRef = useRef<any>(null);
  
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          onDrag(pod.id, marker.getLatLng());
        }
      },
    }),
    [pod.id, onDrag],
  );

  // Calculate pod number within its farm
  const farmPods = pods.filter(p => p.farm_id === pod.farm_id).sort((a, b) => a.id - b.id);
  const podNumber = farmPods.findIndex(p => p.id === pod.id) + 1;

  // Create custom icon for pods
  const podIcon = L.divIcon({
    className: 'pod-marker',
    html: `<div style="
      width: ${pod.size * 20}px; 
      height: ${pod.size * 20}px; 
      background-color: lightgreen; 
      border: 2px solid green; 
      border-radius: 50%; 
      display: flex; 
      align-items: center; 
      justify-content: center;
      font-size: 10px;
      font-weight: bold;
      color: darkgreen;
      cursor: move;
    ">${podNumber}</div>`,
    iconSize: [pod.size * 20, pod.size * 20],
    iconAnchor: [pod.size * 10, pod.size * 10],
  });

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={[pod.position.coordinates[1], pod.position.coordinates[0]]}
      ref={markerRef}
      icon={podIcon}
    >
      <Popup>
        <div>
          <h4>{pod.name}</h4>
          <p>Size: {pod.size.toFixed(1)}</p>
          <p>Farm ID: {pod.farm_id}</p>
          <p>Mussels: {pod.mussel_count || 50}</p>
          <p><em>Drag to move this pod</em></p>
        </div>
      </Popup>
    </Marker>
  );
}
function DraggableFarmPolygon({ 
  farm, 
  isSelected, 
  onSelect, 
  onUpdate 
}: { 
  farm: Farm; 
  isSelected: boolean; 
  onSelect: () => void;
  onUpdate: (farm: Farm, newCoordinates: number[][]) => void;
}) {
  return (
    <Polygon
      positions={farm.geometry.coordinates[0].map(coord => [coord[1], coord[0]])}
      pathOptions={{ 
        color: isSelected ? 'orange' : 'blue', 
        fillColor: isSelected ? 'orange' : 'lightblue', 
        fillOpacity: 0.3,
        weight: isSelected ? 4 : 2
      }}
      eventHandlers={{
        click: onSelect
      }}
    >
      <Popup>
        <div>
          <h3>{farm.name}</h3>
          <p>Farm ID: {farm.id}</p>
          <p>Created: {new Date(farm.created_at).toLocaleDateString()}</p>
        </div>
      </Popup>
    </Polygon>
  );
}

// Component for handling map clicks during farm creation
function FarmCreationHandler({ onPolygonComplete }: { onPolygonComplete: (coordinates: number[][]) => void }) {
  const [points, setPoints] = useState<LatLng[]>([]);
  const [isProcessingClick, setIsProcessingClick] = useState(false);

  useMapEvents({
    click: (e) => {
      if (isProcessingClick) return; // Prevent click during double-click processing
      
      setTimeout(() => {
        if (isProcessingClick) return; // Double-click occurred, skip this click
        
        // Check if clicking on the first point to close polygon
        if (points.length >= 3) {
          const firstPoint = points[0];
          const clickedPoint = e.latlng;
          const distance = firstPoint.distanceTo(clickedPoint);
          
          // If clicking within 100 meters of the first point, close the polygon
          if (distance < 100) {
            const polygonCoords = [...points, points[0]].map(point => [point.lng, point.lat]);
            console.log('Closing polygon by clicking first point:', polygonCoords);
            onPolygonComplete(polygonCoords);
            setPoints([]);
            return;
          }
        }
        
        // Add new point
        const newPoints = [...points, e.latlng];
        setPoints(newPoints);
        console.log(`Added point ${newPoints.length}:`, e.latlng);
      }, 200); // Small delay to allow double-click to be detected
    },
    dblclick: (e) => {
      setIsProcessingClick(true);
      e.originalEvent.preventDefault();
      console.log('Double-click detected, points:', points.length);
      
      if (points.length >= 2) {
        // Don't add the double-click point, just close with existing points
        const polygonCoords = [...points, points[0]].map(point => [point.lng, point.lat]);
        console.log('Closing polygon with double-click:', polygonCoords);
        onPolygonComplete(polygonCoords);
        setPoints([]);
      } else {
        alert('You need at least 3 points to create a farm. Continue clicking to add more points.');
      }
      
      setTimeout(() => setIsProcessingClick(false), 300);
    },
    keydown: (e) => {
      if (e.originalEvent.key === 'Enter' && points.length >= 3) {
        const polygonCoords = [...points, points[0]].map(point => [point.lng, point.lat]);
        console.log('Closing polygon with Enter key:', polygonCoords);
        onPolygonComplete(polygonCoords);
        setPoints([]);
      } else if (e.originalEvent.key === 'Escape') {
        console.log('Canceling polygon creation');
        setPoints([]);
      }
    }
  });

  return (
    <>
      {/* Show current drawing progress */}
      {points.length > 0 && (
        <Polyline 
          positions={points} 
          pathOptions={{ color: 'red', weight: 3, dashArray: '5, 10' }}
        />
      )}
      
      {/* Show preview line back to first point when we have 3+ points */}
      {points.length >= 3 && (
        <Polyline 
          positions={[points[points.length - 1], points[0]]} 
          pathOptions={{ color: 'red', weight: 2, dashArray: '10, 10', opacity: 0.5 }}
        />
      )}
      
      {/* Show markers for clicked points */}
      {points.map((point, index) => (
        <Circle
          key={index}
          center={point}
          radius={index === 0 && points.length >= 3 ? 30 : 20}
          pathOptions={{ 
            color: index === 0 && points.length >= 3 ? 'green' : 'red', 
            fillColor: index === 0 && points.length >= 3 ? 'green' : 'red', 
            fillOpacity: index === 0 && points.length >= 3 ? 0.9 : 0.7 
          }}
        />
      ))}
    </>
  );
}

// Component for handling reef placement
function ReefPlacementHandler({ onReefPlace }: { onReefPlace: (position: [number, number]) => void }) {
  useMapEvents({
    click: (e) => {
      onReefPlace([e.latlng.lng, e.latlng.lat]);
    },
  });

  return null;
}

// Component for handling area selection for biodiversity prediction
function AreaSelectionHandler({ onAreaComplete }: { onAreaComplete: (coordinates: number[][]) => void }) {
  const [points, setPoints] = useState<LatLng[]>([]);
  const [isProcessingClick, setIsProcessingClick] = useState(false);

  useMapEvents({
    click: (e) => {
      if (isProcessingClick) return;
      
      setTimeout(() => {
        if (isProcessingClick) return;
        
        // Check if clicking on the first point to close area
        if (points.length >= 3) {
          const firstPoint = points[0];
          const clickedPoint = e.latlng;
          const distance = firstPoint.distanceTo(clickedPoint);
          
          if (distance < 100) {
            const areaCoords = [...points, points[0]].map(point => [point.lng, point.lat]);
            console.log('Closing area by clicking first point:', areaCoords);
            onAreaComplete(areaCoords);
            setPoints([]);
            return;
          }
        }
        
        const newPoints = [...points, e.latlng];
        setPoints(newPoints);
        console.log(`Added point ${newPoints.length}:`, e.latlng);
      }, 200);
    },
    dblclick: (e) => {
      setIsProcessingClick(true);
      e.originalEvent.preventDefault();
      console.log('Double-click detected, points:', points.length);
      
      if (points.length >= 2) {
        const areaCoords = [...points, points[0]].map(point => [point.lng, point.lat]);
        console.log('Closing area with double-click:', areaCoords);
        onAreaComplete(areaCoords);
        setPoints([]);
      } else {
        alert('You need at least 3 points to select an area. Continue clicking to add more points.');
      }
      
      setTimeout(() => setIsProcessingClick(false), 300);
    },
    keydown: (e) => {
      if (e.originalEvent.key === 'Enter' && points.length >= 3) {
        const areaCoords = [...points, points[0]].map(point => [point.lng, point.lat]);
        console.log('Closing area with Enter key:', areaCoords);
        onAreaComplete(areaCoords);
        setPoints([]);
      } else if (e.originalEvent.key === 'Escape') {
        console.log('Canceling area selection');
        setPoints([]);
      }
    }
  });

  return (
    <>
      {/* Show current drawing progress */}
      {points.length > 0 && (
        <Polyline 
          positions={points} 
          pathOptions={{ color: 'purple', weight: 3, dashArray: '5, 10' }}
        />
      )}
      
      {/* Show preview line back to first point when we have 3+ points */}
      {points.length >= 3 && (
        <Polyline 
          positions={[points[points.length - 1], points[0]]} 
          pathOptions={{ color: 'purple', weight: 2, dashArray: '10, 10', opacity: 0.5 }}
        />
      )}
      
      {/* Show markers for clicked points */}
      {points.map((point, index) => (
        <Circle
          key={index}
          center={point}
          radius={index === 0 && points.length >= 3 ? 30 : 20}
          pathOptions={{ 
            color: index === 0 && points.length >= 3 ? 'green' : 'purple', 
            fillColor: index === 0 && points.length >= 3 ? 'green' : 'purple', 
            fillOpacity: index === 0 && points.length >= 3 ? 0.9 : 0.7 
          }}
        />
      ))}
    </>
  );
}

// Component for tracking zoom changes
function ZoomTracker({ onZoomChange }: { onZoomChange: (zoom: number) => void }) {
  useMapEvents({
    zoomend: (e) => {
      onZoomChange(e.target.getZoom());
    },
  });

  return null;
}

// Component for rendering farm clusters at low zoom levels
function FarmCluster({ 
  farms, 
  onFarmClick 
}: { 
  farms: Farm[]; 
  onFarmClick: (farm: Farm) => void;
}) {
  if (farms.length === 0) return null;

  // Calculate center point of all farms
  const avgLat = farms.reduce((sum, farm) => {
    const coords = farm.geometry.coordinates[0];
    const farmCenterLat = coords.reduce((s: number, coord: number[]) => s + coord[1], 0) / coords.length;
    return sum + farmCenterLat;
  }, 0) / farms.length;

  const avgLng = farms.reduce((sum, farm) => {
    const coords = farm.geometry.coordinates[0];
    const farmCenterLng = coords.reduce((s: number, coord: number[]) => s + coord[0], 0) / coords.length;
    return sum + farmCenterLng;
  }, 0) / farms.length;

  const clusterIcon = L.divIcon({
    className: 'farm-cluster',
    html: `<div style="
      width: 40px; 
      height: 40px; 
      background-color: #4CAF50; 
      border: 3px solid #2E7D32; 
      border-radius: 50%;
      display: flex; 
      align-items: center; 
      justify-content: center;
      font-size: 14px;
      font-weight: bold;
      color: white;
      cursor: pointer;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    ">${farms.length}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });

  return (
    <Marker
      position={[avgLat, avgLng]}
      icon={clusterIcon}
      eventHandlers={{
        click: () => {
          // For now, just click the first farm in the cluster
          if (farms.length > 0) {
            onFarmClick(farms[0]);
          }
        }
      }}
    >
      <Popup>
        <div>
          <h3>Farm Cluster</h3>
          <p>{farms.length} farms in this area</p>
          <ul style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {farms.map(farm => (
              <li key={farm.id} style={{ cursor: 'pointer', padding: '2px 0' }} onClick={() => onFarmClick(farm)}>
                {farm.name}
              </li>
            ))}
          </ul>
        </div>
      </Popup>
    </Marker>
  );
}

// Component for rendering reef clusters at low zoom levels
function ReefCluster({ 
  reefs, 
  onReefClick 
}: { 
  reefs: ArtificialReef[]; 
  onReefClick: (reef: ArtificialReef) => void;
}) {
  if (reefs.length === 0) return null;

  // If only one reef, show it individually
  if (reefs.length === 1) {
    const reef = reefs[0];
    return (
      <Marker
        position={[reef.position.coordinates[1], reef.position.coordinates[0]]}
        icon={L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        })}
        eventHandlers={{
          click: () => onReefClick(reef)
        }}
      >
        <Popup>
          <div>
            <h4>{reef.name}</h4>
            <p>Detection Range: {reef.detection_range}m</p>
            {reef.volume_in_cm3 && <p>Volume: {reef.volume_in_cm3} cm³</p>}
          </div>
        </Popup>
      </Marker>
    );
  }

  // Calculate center point of all reefs
  const avgLat = reefs.reduce((sum, reef) => sum + reef.position.coordinates[1], 0) / reefs.length;
  const avgLng = reefs.reduce((sum, reef) => sum + reef.position.coordinates[0], 0) / reefs.length;

  const clusterIcon = L.divIcon({
    className: 'reef-cluster',
    html: `<div style="
      width: 40px; 
      height: 40px; 
      background-color: #F44336; 
      border: 3px solid #C62828; 
      border-radius: 50%;
      display: flex; 
      align-items: center; 
      justify-content: center;
      font-size: 14px;
      font-weight: bold;
      color: white;
      cursor: pointer;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    ">${reefs.length}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });

  return (
    <Marker
      position={[avgLat, avgLng]}
      icon={clusterIcon}
      eventHandlers={{
        click: () => {
          // Click the first reef in the cluster
          if (reefs.length > 0) {
            onReefClick(reefs[0]);
          }
        }
      }}
    >
      <Popup>
        <div>
          <h3>Reef Cluster</h3>
          <p>{reefs.length} artificial reefs in this area</p>
          <ul style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {reefs.map(reef => (
              <li key={reef.id} style={{ cursor: 'pointer', padding: '2px 0' }} onClick={() => onReefClick(reef)}>
                {reef.name}
              </li>
            ))}
          </ul>
        </div>
      </Popup>
    </Marker>
  );
}

export const Map: React.FC<MapProps> = ({ mode, setMode }) => {
  const [farms, setFarms] = useState<Farm[]>([]);
  const [pods, setPods] = useState<Pod[]>([]);
  const [reefs, setReefs] = useState<ArtificialReef[]>([]);
  const [selectedReef, setSelectedReef] = useState<ArtificialReef | null>(null);
  const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null);
  const [editingReef, setEditingReef] = useState<ArtificialReef | null>(null);
  const [reefFormData, setReefFormData] = useState({
    name: '',
    detection_range: 500,
    fish_species_count: 10,
    volume_in_cm3: null as number | null
  });
  const [reefSurroundings, setReefSurroundings] = useState<ReefSurroundings | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [networkReef, setNetworkReef] = useState<any>(null);
  const [mapZoom, setMapZoom] = useState(10);
  const [predictionResult, setPredictionResult] = useState<{ abundance: number; richness: number } | null>(null);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [areaPredictionResult, setAreaPredictionResult] = useState<{ abundance: number; richness: number; numReefs: number } | null>(null);
  const [selectedAreaBounds, setSelectedAreaBounds] = useState<number[][] | null>(null);
  const [showInstructions, setShowInstructions] = useState(false); // Closed by default
  const [showBackground, setShowBackground] = useState(true); // Open by default
  const [predictionHistory, setPredictionHistory] = useState<Array<{ 
    id: number; 
    timestamp: Date; 
    abundance: number; 
    richness: number; 
    numReefs: number;
    bounds: number[][];
  }>>([]);
  const mapRef = useRef<any>(null);

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [farmsData, podsData, reefsData] = await Promise.all([
        apiService.getFarms(),
        apiService.getPods(),
        apiService.getReefs(),
      ]);
      setFarms(farmsData);
      setPods(podsData);
      setReefs(reefsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Targeted farm deletion that updates state locally without full reload
  const handleFarmDeleted = (deletedFarmId: number) => {
    // Remove farm from local state
    setFarms(farms.filter(farm => farm.id !== deletedFarmId));
    // Remove all pods associated with the deleted farm
    setPods(pods.filter(pod => pod.farm_id !== deletedFarmId));
    // Clear selection if the deleted farm was selected
    if (selectedFarm?.id === deletedFarmId) {
      setSelectedFarm(null);
    }
  };

  const handleReefDeleted = (deletedReefId: number) => {
    // Remove reef from local state
    setReefs(reefs.filter(reef => reef.id !== deletedReefId));
    // Clear selection if the deleted reef was selected
    if (selectedReef?.id === deletedReefId) {
      setSelectedReef(null);
    }
  };

  const handleCreateFarm = async (coordinates: number[][]) => {
    try {
      console.log('Creating farm with coordinates:', coordinates);
      
      // Validate coordinates
      if (!coordinates || coordinates.length < 4) {
        throw new Error('Invalid coordinates: Need at least 3 points plus closing point');
      }

      const farmName = prompt('Enter farm name:') || 'New Farm';
      const podCountStr = prompt('How many pods would you like in this farm?') || '3';
      const podCount = parseInt(podCountStr);
      
      if (isNaN(podCount) || podCount < 0) {
        throw new Error('Invalid pod count');
      }

      // Use the custom polygon coordinates directly instead of creating a standard 200x200m farm
      const customCoordinates = coordinates;

      console.log('Sending farm creation request...');
      
      const newFarm = await apiService.createFarm({
        name: farmName,
        geometry: {
          type: 'Polygon',
          coordinates: [customCoordinates]
        }
      });
      
      console.log('Farm created successfully:', newFarm);
      setFarms([...farms, newFarm]);
      setMode('view');
      
      // Create pods for the new farm
      if (podCount > 0) {
        console.log('Creating pods...');
        await createSamplePods(newFarm.id, customCoordinates, podCount);
      }
    } catch (error) {
      console.error('Error creating farm:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to create farm: ${errorMessage}`);
    }
  };

  const handlePodDrag = async (podId: number, newLatlng: any) => {
    try {
      const pod = pods.find(p => p.id === podId);
      if (!pod) return;
      
      const farm = farms.find(f => f.id === pod.farm_id);
      if (!farm) return;
      
      const newPosition: [number, number] = [newLatlng.lng, newLatlng.lat];
      
      // Get farm polygon coordinates
      const farmCoords = farm.geometry.coordinates[0];
      const polygonCoords = farmCoords.slice(0, -1); // Remove closing point
      
      // Get positions of other pods in the same farm
      const otherPodPositions = pods
        .filter(p => p.farm_id === pod.farm_id && p.id !== podId)
        .map(p => [p.position.coordinates[0], p.position.coordinates[1]] as [number, number]);
      
      // Validate new position
      if (!isValidPodPosition(newPosition, polygonCoords, otherPodPositions, 20)) {
        alert('Cannot place pod here: must be within farm boundaries, at least 10m from edges, and 20m from other pods.');
        return;
      }
      
      const updatedPod = await apiService.updatePod(podId, {
        position: {
          type: 'Point',
          coordinates: [newLatlng.lng, newLatlng.lat]
        }
      });
      
      // Update pods state
      setPods(pods.map((pod: any) => pod.id === podId ? updatedPod : pod));
    } catch (error) {
      console.error('Error updating pod position:', error);
      alert('Failed to update pod position');
    }
  };

  const handleReefDrag = async (reefId: number, newLatlng: any) => {
    try {
      const updatedReef = await apiService.updateReef(reefId, {
        position: {
          type: 'Point',
          coordinates: [newLatlng.lng, newLatlng.lat]
        }
      });
      
      // Update reefs state
      setReefs(reefs.map((reef: any) => reef.id === reefId ? updatedReef : reef));
      
      // If this reef is selected, reload its surroundings
      if (selectedReef?.id === reefId) {
        const surroundings = await apiService.getReefSurroundings(reefId);
        setReefSurroundings(surroundings);
      }
    } catch (error) {
      console.error('Error updating reef position:', error);
      alert('Failed to update reef position');
    }
  };

  const handleShowNetwork = (reef: any) => {
    // Check if reef is selected and has surroundings data
    if (selectedReef?.id === reef.id && reefSurroundings) {
      setNetworkReef(reef);
      setShowNetworkModal(true);
    } else {
      alert('Please click the reef first to load network data, then try Show Network again.');
    }
  };

  const handleFarmZoom = (farm: Farm) => {
    if (mapRef.current) {
      const map = mapRef.current;
      
      // Calculate farm bounds
      const coords = farm.geometry.coordinates[0];
      const lats = coords.map(coord => coord[1]);
      const lngs = coords.map(coord => coord[0]);
      
      const bounds = [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)]
      ];
      
      // Fit the map to the farm bounds with some padding
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  const handleReefZoom = (reef: ArtificialReef) => {
    if (mapRef.current) {
      const map = mapRef.current;
      const lat = reef.position.coordinates[1];
      const lng = reef.position.coordinates[0];
      
      // Zoom to reef location with appropriate zoom level
      map.setView([lat, lng], 15);
    }
  };

  const handleFarmUpdate = (updatedFarm: Farm, newCoordinates: number[][]) => {
    setFarms(farms.map((farm: any) => 
      farm.id === updatedFarm.id ? updatedFarm : farm
    ));
  };

  // Helper function to check if a point is inside a polygon using ray casting algorithm
  // This version adds a small buffer to ensure points are strictly inside, not on the border
  const isPointInPolygon = (point: [number, number], polygon: number[][], buffer: number = 0): boolean => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    if (!inside) return false;
    
    // If buffer is specified, check minimum distance from all polygon edges
    if (buffer > 0) {
      for (let i = 0; i < polygon.length; i++) {
        const j = (i + 1) % polygon.length;
        const distanceToEdge = getDistanceToLineSegment(point, polygon[i], polygon[j]);
        if (distanceToEdge < buffer) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Helper function to calculate distance from a point to a line segment
  const getDistanceToLineSegment = (point: [number, number], lineStart: number[], lineEnd: number[]): number => {
    const [px, py] = point;
    const [x1, y1] = lineStart;
    const [x2, y2] = lineEnd;
    
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) {
      param = dot / lenSq;
    }
    
    let xx, yy;
    
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = px - xx;
    const dy = py - yy;
    
    // Convert to meters
    return getDistanceInMeters(point, [xx, yy]);
  };

  // Helper function to calculate distance between two points in meters
  const getDistanceInMeters = (point1: [number, number], point2: [number, number]): number => {
    const [lng1, lat1] = point1;
    const [lng2, lat2] = point2;
    
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Helper function to check if a position is valid (inside polygon and min distance from other pods)
  const isValidPodPosition = (position: [number, number], polygon: number[][], existingPositions: [number, number][], minDistance: number = 20): boolean => {
    // First check if point is inside polygon (without buffer for now)
    const isInside = isPointInPolygon(position, polygon, 0);
    if (!isInside) {
      return false;
    }
    
    // Check 10m buffer from farm boundaries by calculating actual distance to edges
    const bufferDistance = 10; // meters
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      const distanceToEdge = getDistanceToLineSegment(position, polygon[i], polygon[j]);
      if (distanceToEdge < bufferDistance) {
        return false;
      }
    }
    
    // Check minimum distance from existing pods
    for (const existingPos of existingPositions) {
      if (getDistanceInMeters(position, existingPos) < minDistance) {
        return false;
      }
    }
    
    return true;
  };

    const createSamplePods = async (farmId: number, farmCoords: number[][], numPods: number) => {
    try {
      const newPods: Pod[] = [];
      const placedPositions: [number, number][] = [];
      const minDistance = 20; // meters
      
      // Calculate farm bounds for initial search area with some padding
      const lngs = farmCoords.map(coord => coord[0]);
      const lats = farmCoords.map(coord => coord[1]);
      
      // Add padding to search area to ensure we don't miss edge positions
      const lngPadding = 0.001; // roughly 100m
      const latPadding = 0.001; // roughly 100m
      
      const minLng = Math.min(...lngs) - lngPadding;
      const maxLng = Math.max(...lngs) + lngPadding;
      const minLat = Math.min(...lats) - latPadding;
      const maxLat = Math.max(...lats) + latPadding;
      
      // Remove the closing point for polygon calculations (last point same as first)
      const polygonCoords = farmCoords.slice(0, -1);
      
      // Create a proper grid-based placement system
      const gridSpacing = 22; // meters - spacing between grid points (slightly more than 20m minimum distance)
      const latDegreeToMeters = 111000;
      const lngDegreeToMeters = 111000 * Math.cos((minLat + maxLat) / 2 * Math.PI / 180);
      
      const latStep = gridSpacing / latDegreeToMeters;
      const lngStep = gridSpacing / lngDegreeToMeters;
      
      const gridPositions: Array<{position: [number, number], row: number, col: number}> = [];
      
      // Generate grid of candidate positions with row/column tracking
      console.log('Farm bounds:', { minLat, maxLat, minLng, maxLng });
      console.log('Grid steps:', { latStep, lngStep });
      console.log('Polygon coords:', polygonCoords);
      
      let totalChecked = 0;
      let insidePolygon = 0;
      let passedBuffer = 0;
      
      let row = 0;
      for (let lat = minLat; lat <= maxLat; lat += latStep) {
        let col = 0;
        for (let lng = minLng; lng <= maxLng; lng += lngStep) {
          totalChecked++;
          const position: [number, number] = [lng, lat];
          
          // Check each step of validation
          const isInside = isPointInPolygon(position, polygonCoords, 0);
          if (isInside) {
            insidePolygon++;
            
            // TEMPORARILY SKIP BUFFER CHECK FOR DEBUGGING
            passedBuffer++;
            gridPositions.push({position, row, col});
            
            // Log first few valid positions for debugging
            if (gridPositions.length <= 5) {
              console.log(`Valid position ${gridPositions.length}:`, position);
            }
          }
          col++;
        }
        row++;
      }
      
      console.log(`Grid analysis: ${totalChecked} total positions checked`);
      console.log(`${insidePolygon} inside polygon`);
      console.log(`${passedBuffer} passed buffer check (buffer disabled for testing)`);
      console.log(`Generated ${gridPositions.length} valid grid positions for ${numPods} requested pods`);
      
      // Sort by row first, then column to maintain grid pattern
      gridPositions.sort((a, b) => {
        if (a.row !== b.row) return a.row - b.row;
        return a.col - b.col;
      });
      
      // Place pods in grid order, respecting minimum distance
      for (const gridPoint of gridPositions) {
        if (newPods.length >= numPods) break;
        
        if (isValidPodPosition(gridPoint.position, polygonCoords, placedPositions, minDistance)) {
          try {
            const pod = await apiService.createPod({
              farm_id: farmId,
              name: `Pod ${newPods.length + 1}`,
              position: {
                type: 'Point',
                coordinates: gridPoint.position
              },
              size: 1.0
            });
            newPods.push(pod);
            placedPositions.push(gridPoint.position);
          } catch (error) {
            console.error('Error creating individual pod:', error);
          }
        }
      }
      
      // If grid approach didn't place enough pods, try fill-in approach with smaller spacing
      if (newPods.length < numPods) {
        const finerGridSpacing = 22; // slightly smaller spacing for fill-in
        const finerLatStep = finerGridSpacing / latDegreeToMeters;
        const finerLngStep = finerGridSpacing / lngDegreeToMeters;
        
        // Create offset grid positions (half-step offset)
        const offsetGridPositions: [number, number][] = [];
        for (let lat = minLat + finerLatStep/2; lat <= maxLat; lat += finerLatStep) {
          for (let lng = minLng + finerLngStep/2; lng <= maxLng; lng += finerLngStep) {
            const position: [number, number] = [lng, lat];
            if (isValidPodPosition(position, polygonCoords, placedPositions, minDistance)) {
              offsetGridPositions.push(position);
            }
          }
        }
        
        // Place remaining pods from offset grid
        for (const position of offsetGridPositions) {
          if (newPods.length >= numPods) break;
          
          if (isValidPodPosition(position, polygonCoords, placedPositions, minDistance)) {
            try {
              const pod = await apiService.createPod({
                farm_id: farmId,
                name: `Pod ${newPods.length + 1}`,
                position: {
                  type: 'Point',
                  coordinates: position
                },
                size: 1.0
              });
              newPods.push(pod);
              placedPositions.push(position);
            } catch (error) {
              console.error('Error creating individual pod:', error);
            }
          }
        }
      }
      
      // Check if we couldn't place all requested pods
      if (newPods.length < numPods) {
        const missingPods = numPods - newPods.length;
        alert(`Warning: Could only place ${newPods.length} out of ${numPods} requested pods. ` +
              `Not enough space in the farm area while maintaining 20m minimum distance between pods ` +
              `and 10m buffer from farm boundaries. ` +
              `Consider reducing the number of pods by ${missingPods} or creating a larger farm.`);
      }
      
      setPods([...pods, ...newPods]);
    } catch (error) {
      console.error('Error creating pods:', error);
      alert('Failed to create pods. Please try again.');
    }
  };

  const handleCreateReef = async (position: [number, number]) => {
    try {
      const reefName = prompt('Enter reef name:') || 'New Reef';
      const detectionRange = parseInt(prompt('Detection range (meters):') || '200');
      const volumeStr = prompt('Volume in cm³ (optional):');
      const volume = volumeStr ? parseInt(volumeStr) : null;
      
      const newReef = await apiService.createReef({
        name: reefName,
        position: {
          type: 'Point',
          coordinates: position
        },
        detection_range: detectionRange,
        fish_species_count: 10, // Default value
        volume_in_cm3: volume || undefined
      });
      
      setReefs([...reefs, newReef]);
      setMode('view');
    } catch (error) {
      console.error('Error creating reef:', error);
    }
  };

  const handleUpdateReef = async (reefId: number, updates: any) => {
    try {
      // Convert null to undefined for the API
      const updatesToSend = {
        ...updates,
        volume_in_cm3: updates.volume_in_cm3 === null ? undefined : updates.volume_in_cm3
      };
      const updatedReef = await apiService.updateReef(reefId, updatesToSend);
      setReefs(reefs.map(reef => reef.id === reefId ? updatedReef : reef));
      setEditingReef(null);
      setMode('view');
    } catch (error) {
      console.error('Error updating reef:', error);
      alert('Failed to update reef. Please try again.');
    }
  };

  const handleStartEditReef = (reef: ArtificialReef) => {
    setEditingReef(reef);
    setReefFormData({
      name: reef.name,
      detection_range: reef.detection_range,
      fish_species_count: reef.fish_species_count,
      volume_in_cm3: reef.volume_in_cm3 || null
    });
  };

  const handleCancelEditReef = () => {
    setEditingReef(null);
    setMode('view');
  };

  const handleReefClick = async (reef: ArtificialReef) => {
    try {
      setSelectedReef(reef);
      setPredictionResult(null); // Clear previous prediction when selecting new reef
      const surroundings = await apiService.getReefSurroundings(reef.id);
      setReefSurroundings(surroundings);
    } catch (error) {
      console.error('Error loading reef surroundings:', error);
    }
  };

  const handlePredictBiodiversity = async (reef: ArtificialReef) => {
    if (!reef.volume_in_cm3) {
      alert('Volume is required to predict biodiversity. Please edit the reef and add a volume.');
      return;
    }

    try {
      setPredictionLoading(true);
      const result = await apiService.getReefPrediction(reef.id);
      // The API returns arrays with single values
      setPredictionResult({
        abundance: result.abundance[0],
        richness: result.richness[0]
      });
    } catch (error) {
      console.error('Error predicting biodiversity:', error);
      alert('Failed to predict biodiversity. Please try again.');
    } finally {
      setPredictionLoading(false);
    }
  };

  const handleAreaSelection = async (coordinates: number[][]) => {
    try {
      // Find reefs within the selected area
      const reefsInArea = reefs.filter(reef => {
        const reefLng = reef.position.coordinates[0];
        const reefLat = reef.position.coordinates[1];
        return isPointInPolygon([reefLng, reefLat], coordinates.slice(0, -1), 0);
      });

      const numReefs = reefsInArea.length;
      console.log(`Found ${numReefs} reefs in selected area`);
      
      if (numReefs === 0) {
        alert('No reefs found in the selected area. Please select an area containing reefs.');
        setMode('view');
        return;
      }

      // Filter out reefs without volume data
      const reefsWithVolume = reefsInArea.filter(reef => reef.volume_in_cm3 && reef.volume_in_cm3 > 0);
      
      if (reefsWithVolume.length === 0) {
        alert('No reefs with volume data found in the selected area. Please ensure reefs have volume_in_cm3 set.');
        setMode('view');
        return;
      }

      // Extract volumes and positions
      const volumes_in_cm3 = reefsWithVolume.map(reef => reef.volume_in_cm3!);
      const lons_in_decdeg_epsg4326 = reefsWithVolume.map(reef => reef.position.coordinates[0]);
      const lats_in_decdeg_epsg4326 = reefsWithVolume.map(reef => reef.position.coordinates[1]);

      console.log(`Calling prediction with ${reefsWithVolume.length} reefs with volume data`);

      setPredictionLoading(true);
      const result = await apiService.getAreaPrediction(volumes_in_cm3, lons_in_decdeg_epsg4326, lats_in_decdeg_epsg4326);
      
      // Store the result and selected area
      const predictionData = {
        abundance: Array.isArray(result.abundance) ? result.abundance[0] : result.abundance,
        richness: Array.isArray(result.richness) ? result.richness[0] : result.richness,
        numReefs: reefsWithVolume.length
      };
      
      setAreaPredictionResult(predictionData);
      setSelectedAreaBounds(coordinates);
      
      // Add to prediction history
      const newPrediction = {
        id: Date.now(),
        timestamp: new Date(),
        abundance: predictionData.abundance,
        richness: predictionData.richness,
        numReefs: reefsWithVolume.length,
        bounds: coordinates
      };
      setPredictionHistory([newPrediction, ...predictionHistory]);
      
      setMode('view');
    } catch (error) {
      console.error('Error predicting area biodiversity:', error);
      alert('Failed to predict area biodiversity. Please try again.');
      setMode('view');
    } finally {
      setPredictionLoading(false);
    }
  };

  const handleDeleteReef = async (reefId: number) => {
    if (window.confirm('Are you sure you want to delete this reef?')) {
      try {
        console.log('Deleting reef with ID:', reefId);
        await apiService.deleteReef(reefId);
        console.log('Reef deleted successfully');
        setReefs(reefs.filter(reef => reef.id !== reefId));
        setSelectedReef(null);
        setReefSurroundings(null);
        alert('Reef deleted successfully');
      } catch (error) {
        console.error('Error deleting reef:', error);
        alert('Failed to delete reef. Please try again.');
      }
    }
  };

  const handlePredictionClick = (bounds: number[][]) => {
    // Show the polygon for the clicked prediction
    setSelectedAreaBounds(bounds);
    
    // Optionally zoom to the area
    if (mapRef.current) {
      const lats = bounds.map(coord => coord[1]);
      const lngs = bounds.map(coord => coord[0]);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      
      const boundsObj = new LatLngBounds(
        [minLat, minLng],
        [maxLat, maxLng]
      );
      
      mapRef.current.fitBounds(boundsObj, { padding: [50, 50] });
    }
  };

  if (loading) {
    return <div className="loading">Loading map data...</div>;
  }

  return (
    <div className="map-container">
      <div className="map-controls">
        <button 
          className={mode === 'create-farm' ? 'active' : ''}
          onClick={() => setMode('create-farm')}
        >
          Create Farm
        </button>
        <button 
          className={mode === 'create-reef' ? 'active' : ''}
          onClick={() => setMode('create-reef')}
        >
          Place Reef
        </button>
        <button 
          className={mode === 'edit-farm' ? 'active' : ''}
          onClick={() => setMode('edit-farm')}
        >
          Edit Farm
        </button>
        <button 
          className={mode === 'edit-reef' ? 'active' : ''}
          onClick={() => setMode('edit-reef')}
        >
          Edit Reef
        </button>
        <button 
          className={mode === 'predict-area' ? 'active' : ''}
          onClick={() => {
            setSelectedAreaBounds(null);
            setAreaPredictionResult(null);
            setMode('predict-area');
          }}
          style={{ backgroundColor: mode === 'predict-area' ? '#9C27B0' : '#2196F3' }}
        >
          Predict Biodiversity in Area
        </button>
        <button 
          className="background-button"
          onClick={() => {
            setShowBackground(!showBackground);
            if (!showBackground) setShowInstructions(false); // Close Help when opening Background
          }}
          style={{ 
            backgroundColor: showBackground ? '#9c27b0' : '#607d8b',
            marginLeft: 'auto'
          }}
          title="Show/Hide Background Information"
        >
          📖 Background
        </button>
        <button 
          className="help-button"
          onClick={() => {
            setShowInstructions(!showInstructions);
            if (!showInstructions) setShowBackground(false); // Close Background when opening Help
          }}
          style={{ 
            backgroundColor: showInstructions ? '#ffc107' : '#17a2b8',
            marginLeft: '8px'
          }}
          title="Show/Hide Instructions"
        >
          ❓ Help
        </button>
      </div>

      <div className="map-wrapper">
        {/* Background Panel */}
        {showBackground && <BackgroundPanel onClose={() => setShowBackground(false)} />}
        
        {/* Instructions Panel */}
        {showInstructions && <InstructionsPanel onClose={() => setShowInstructions(false)} />}
        
        <MapContainer
          ref={mapRef}
          className="leaflet-map"
        >
          <MapViewportPreserver />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Scale control in lower left corner */}
          <ScaleControl position="bottomleft" metric={true} imperial={false} />

          {/* Track zoom changes */}
          <ZoomTracker onZoomChange={setMapZoom} />

          {/* Render farms */}
          {mapZoom >= 14 ? (
            // Show individual farms when zoomed in (zoom level 14 and above)
            farms.map((farm: any) => (
              <DraggableFarmPolygon 
                key={farm.id}
                farm={farm}
                isSelected={selectedFarm?.id === farm.id}
                onSelect={() => setSelectedFarm(farm)}
                onUpdate={handleFarmUpdate}
              />
            ))
          ) : (
            // Show farm clusters when zoomed out (below zoom level 14)
            <FarmCluster 
              farms={farms}
              onFarmClick={(farm) => {
                setSelectedFarm(farm);
                handleFarmZoom(farm);
              }}
            />
          )}

          {/* Render pods - only show when zoomed in enough (farms not clustered) */}
          {mapZoom >= 14 && pods.map((pod: any) => (
            <DraggablePod
              key={pod.id}
              pod={pod}
              pods={pods}
              onDrag={handlePodDrag}
            />
          ))}

          {/* Render artificial reefs - cluster when zoomed out, individual when zoomed in */}
          {mapZoom >= 14 && reefs.map(reef => (
            <React.Fragment key={reef.id}>
              <DraggableReef
                reef={reef}
                onDrag={handleReefDrag}
                onClick={handleReefClick}
                isSelected={selectedReef?.id === reef.id}
                reefSurroundings={selectedReef?.id === reef.id ? reefSurroundings : null}
                onDelete={handleDeleteReef}
                onShowNetwork={handleShowNetwork}
                isEditing={editingReef?.id === reef.id}
                editFormData={reefFormData}
                onFormChange={(field, value) => setReefFormData(prev => ({ ...prev, [field]: value }))}
                onSaveEdit={() => handleUpdateReef(reef.id, reefFormData)}
                onCancelEdit={handleCancelEditReef}
                onStartEdit={() => handleStartEditReef(reef)}
                onPredictBiodiversity={handlePredictBiodiversity}
                predictionResult={selectedReef?.id === reef.id ? predictionResult : null}
                predictionLoading={predictionLoading}
                mode={mode}
              />
              
              {/* Show detection range circle when reef is selected */}
              {selectedReef?.id === reef.id && (
                <Circle
                  center={[reef.position.coordinates[1], reef.position.coordinates[0]]}
                  radius={reef.detection_range}
                  pathOptions={{ color: 'red', fillColor: 'transparent', dashArray: '5, 5' }}
                />
              )}
            </React.Fragment>
          ))}

          {mapZoom < 14 && (
            <ReefCluster
              reefs={reefs}
              onReefClick={handleReefClick}
            />
          )}

          {/* Event handlers for different modes */}
          {mode === 'create-farm' && (
            <FarmCreationHandler onPolygonComplete={handleCreateFarm} />
          )}
          {mode === 'create-reef' && (
            <ReefPlacementHandler onReefPlace={handleCreateReef} />
          )}
          {mode === 'predict-area' && (
            <AreaSelectionHandler onAreaComplete={handleAreaSelection} />
          )}

          {/* Show selected area bounds with prediction results */}
          {selectedAreaBounds && areaPredictionResult && (
            <>
              <Polygon
                positions={selectedAreaBounds.map(coord => [coord[1], coord[0]])}
                pathOptions={{ 
                  color: 'purple', 
                  fillColor: 'purple', 
                  fillOpacity: 0.2,
                  weight: 3
                }}
              >
                <Popup>
                  <div style={{ padding: '10px' }}>
                    <h3 style={{ margin: '0 0 10px 0' }}>Area Biodiversity Prediction</h3>
                    <p style={{ margin: '5px 0' }}><strong>Number of Reefs:</strong> {areaPredictionResult.numReefs}</p>
                    <p style={{ margin: '5px 0' }}><strong>Predicted Abundance:</strong> {areaPredictionResult.abundance.toFixed(2)}</p>
                    <p style={{ margin: '5px 0' }}><strong>Predicted Richness:</strong> {areaPredictionResult.richness.toFixed(2)}</p>
                    <button 
                      onClick={() => {
                        setSelectedAreaBounds(null);
                        setAreaPredictionResult(null);
                      }}
                      style={{ marginTop: '10px', padding: '5px 10px' }}
                    >
                      Clear Selection
                    </button>
                  </div>
                </Popup>
              </Polygon>
            </>
          )}
        </MapContainer>
      </div>

      {/* Farm Management Sidebar */}
      <FarmSidebar
        farms={farms}
        pods={pods}
        reefs={reefs}
        onFarmUpdate={loadAllData}
        onFarmDeleted={handleFarmDeleted}
        onFarmSelect={setSelectedFarm}
        onFarmZoom={handleFarmZoom}
        onReefDeleted={handleReefDeleted}
        onReefSelect={setSelectedReef}
        onReefZoom={handleReefZoom}
        selectedFarm={selectedFarm}
        selectedReef={selectedReef}
        predictionHistory={predictionHistory}
        onPredictionClick={handlePredictionClick}
      />

      {mode === 'create-farm' && (
        <div className="instruction">
          Click to add points and create custom farm boundaries. To complete: double-click anywhere, click the green circle (first point), or press Enter.
        </div>
      )}

      {mode === 'create-reef' && (
        <div className="instruction">
          Click on the map to place an artificial reef.
        </div>
      )}

      {mode === 'predict-area' && (
        <div className="instruction">
          Click to draw an area containing reefs. To complete: double-click, click the green circle (first point), or press Enter. The system will count reefs in the area and predict biodiversity.
        </div>
      )}

      {/* Network Modal */}
      {showNetworkModal && networkReef && reefSurroundings && (
        <NetworkModal
          reef={networkReef}
          reefSurroundings={reefSurroundings}
          onClose={() => setShowNetworkModal(false)}
        />
      )}
    </div>
  );
};
